/**
 * Shared refresh controller for version skew. Both skew detectors feed it: the Apollo
 * error link (`setOnClientOutdated`, GraphQL/Heroku boundary) and `apiFetch`'s pinned
 * 404s (`setOnDeploymentOutdated`, Vercel route-handler boundary).
 *
 * Framework-free and dependency-injected so the reload/clock/storage seams are
 * deterministic under test; `./index.ts` wires the browser-backed singleton.
 */

export const SKEW_RELOAD_WINDOW_MS = 120_000;

export type SkewTrigger = 'graphql' | 'route-handler';

/**
 * Non-PII by construction: operation name and deployment ids only. Never widen this
 * to GraphQL variables, response data, or raw error-message bodies — on Canvas and
 * planning operations those carry householdId/account identifiers (plan finding S1).
 */
export type SkewOutdatedEvent = {
  readonly trigger: SkewTrigger;
  readonly operationName: string | null;
  readonly clientDeploymentId: string | null;
};

export type SkewRefreshState =
  /** No incompatibility seen. */
  | 'idle'
  /** Incompatible, but a reload right now would destroy in-flight work. */
  | 'deferred'
  /** Waiting on the user: a reload is safe but we will not take it for them. */
  | 'refresh-ready'
  /** Reloaded too recently — reloading again would loop. */
  | 'breaker-tripped';

/**
 * Canvas's "unsafe to reload" registration: a predicate plus an idle notifier. A single
 * registration rather than a multi-surface registry — Canvas is the only v1 consumer
 * (plan finding SG2).
 */
export type ReloadGate = {
  readonly isReloadUnsafe: () => boolean;
  readonly subscribeToIdle: (onIdle: () => void) => () => void;
};

export type ReloadGateSource = {
  readonly gate: ReloadGate;
  readonly setReloadUnsafe: (unsafe: boolean) => void;
};

/**
 * Adapts a boolean that changes over time (Canvas `isStreaming`) into a `ReloadGate`. Split
 * out of `useSkewReloadGate` so the edge-detection below is testable without a React render.
 */
export function createReloadGateSource(initialUnsafe: boolean): ReloadGateSource {
  let unsafe = initialUnsafe;
  const idleListeners = new Set<() => void>();

  return {
    gate: {
      isReloadUnsafe: () => unsafe,
      subscribeToIdle: (onIdle) => {
        idleListeners.add(onIdle);
        return () => {
          idleListeners.delete(onIdle);
        };
      },
    },

    setReloadUnsafe: (next) => {
      const wasUnsafe = unsafe;
      unsafe = next;
      // Only the unsafe→safe edge is "went idle". Notifying on every set would fire on mount
      // and on each repeat `false`, re-offering a refresh that is already on screen.
      if (wasUnsafe && !next) {
        for (const listener of [...idleListeners]) listener();
      }
    },
  };
}

export type SkewReloadClock = {
  readonly now: () => number;
  readonly readLastReloadAt: () => number | null;
  readonly writeLastReloadAt: (at: number) => void;
};

export type SkewRefreshDeps = {
  readonly clock: SkewReloadClock;
  readonly reload: () => void;
  readonly logOutdated: (event: SkewOutdatedEvent) => void;
};

export type SkewRefreshController = {
  readonly getState: () => SkewRefreshState;
  readonly subscribe: (listener: () => void) => () => void;
  readonly setReloadGate: (gate: ReloadGate | null) => void;
  readonly setAutoReloadEnabled: (enabled: boolean) => void;
  readonly reportOutdated: (event: SkewOutdatedEvent) => void;
  readonly refreshNow: () => void;
};

export function createSkewRefreshController({ clock, reload, logOutdated }: SkewRefreshDeps): SkewRefreshController {
  let state: SkewRefreshState = 'idle';
  let gate: ReloadGate | null = null;
  // Default off, so an incompatibility detected before LaunchDarkly resolves surfaces a
  // manual refresh rather than an unguarded auto-reload.
  let autoReloadEnabled = false;
  let unsubscribeFromIdle: (() => void) | null = null;
  const listeners = new Set<() => void>();

  function setState(next: SkewRefreshState): void {
    if (state === next) return;
    state = next;
    for (const listener of listeners) listener();
  }

  function stopWaitingForIdle(): void {
    unsubscribeFromIdle?.();
    unsubscribeFromIdle = null;
  }

  function waitForIdle(target: ReloadGate): () => void {
    return target.subscribeToIdle(() => {
      stopWaitingForIdle();
      // D3: the finished answer stays on screen and the user chooses when to
      // refresh. Auto-reloading here would yank it as they start reading.
      setState('refresh-ready');
    });
  }

  function performReload(): void {
    // Stamp before reloading: the timestamp is what makes the *next* page load able to
    // tell "I just reloaded for this" from "first time I've seen this".
    clock.writeLastReloadAt(clock.now());
    reload();
  }

  function reloadedWithinWindow(): boolean {
    const lastReloadAt = clock.readLastReloadAt();
    if (lastReloadAt === null) return false;
    const elapsed = clock.now() - lastReloadAt;
    // A negative elapsed means the stored stamp is in the future (clock moved backwards);
    // treat it as recent rather than reloading immediately.
    return elapsed < SKEW_RELOAD_WINDOW_MS;
  }

  return {
    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    setReloadGate(next) {
      if (gate === next) return;
      // Drop the old gate's idle subscription before losing the reference to it. Otherwise the
      // replaced gate keeps our callback and can still drive a transition after deregistering.
      stopWaitingForIdle();
      gate = next;

      if (state !== 'deferred') return;

      // Re-arm on the replacement, because only an unsafe→safe edge on the gate we just
      // dropped would ever have released us. A gate that unregistered (Canvas unmounting) or
      // that reports safe means the work we were protecting is gone, so offer the reload.
      if (next !== null && next.isReloadUnsafe()) {
        unsubscribeFromIdle = waitForIdle(next);
        return;
      }
      setState('refresh-ready');
    },

    setAutoReloadEnabled(enabled) {
      autoReloadEnabled = enabled;
    },

    reportOutdated(event) {
      // Log every occurrence, including ones the states below swallow, so a breaker trip
      // shows up as a Datadog spike instead of being silently absorbed.
      logOutdated(event);

      // Both are already surfaced to the user and awaiting their action; re-reporting
      // must not escalate to a reload or re-enter the gate.
      if (state === 'refresh-ready' || state === 'breaker-tripped') return;

      if (!autoReloadEnabled) {
        stopWaitingForIdle();
        setState('refresh-ready');
        return;
      }

      if (reloadedWithinWindow()) {
        stopWaitingForIdle();
        setState('breaker-tripped');
        return;
      }

      if (gate?.isReloadUnsafe() === true) {
        if (state !== 'deferred') {
          setState('deferred');
          unsubscribeFromIdle = waitForIdle(gate);
        }
        return;
      }

      performReload();
    },

    refreshNow() {
      stopWaitingForIdle();
      performReload();
    },
  };
}
